import Shadow from '$tablet-app/src/app/components/Shadow';
import { ChatResponseOption } from '@akin-home/chat';
import { colors } from '@akin-home/theme';
import Checkbox from 'expo-checkbox';
import React from 'react';
import { StyleSheet } from 'react-native';
import styled from 'styled-components/native';
import { Text } from '../../../components/Text/Text';
import { color } from '../../../theme';
import { getOptionIcon } from '../../functions';
import { ChatTextItem as ChatTextItemType } from '../types';
import ChatTextItemImage from './ChatTextItemImage';

interface Props {
  idx: number;
  item: ChatTextItemType;
  dont_show_icon_options?: boolean;
  onPressOption?: (input: string, optionIdx: number) => void;
}

const ChatTextItem = (props: Props) => {
  const {
    idx,
    dont_show_icon_options,
    item: {
      data: { text, options, image_url, multiple_choice_options },
    },
    onPressOption,
  } = props;

  const handleOptionPress = (value: string) => onPressOption?.(value, idx);

  const show_as_row =
    options?.some((opt) => opt.icon?.length) && !dont_show_icon_options;

  const disabled =
    options?.some((opt) => opt.selected) && !multiple_choice_options;

  return (
    <Container>
      {image_url ? <ChatTextItemImage source={{ uri: image_url }} /> : null}

      {text ? <ChatText>{text}</ChatText> : null}

      {options?.length ? (
        <Options style={show_as_row ? styles.option_row : undefined}>
          {options?.map((option) => (
            <Option
              dont_show_icon_options={dont_show_icon_options}
              key={option.input.value}
              onPress={handleOptionPress}
              disabled={option.selected || disabled}
              option={option}
            />
          ))}
        </Options>
      ) : null}
    </Container>
  );
};

type OptionProps = {
  option: ChatResponseOption;
  disabled?: boolean;
  dont_show_icon_options?: boolean;
  onPress(value: string): void;
};

const Option = ({
  option,
  disabled,
  dont_show_icon_options,
  onPress,
}: OptionProps) => {
  const handleSelect = () => !disabled && onPress(option.input.value);

  const is_checked = !!option.selected;
  const icon = getOptionIcon(option);

  return icon && !dont_show_icon_options ? (
    <OptionWithIconContainer onPress={handleSelect} active={is_checked}>
      <Emoji>{icon}</Emoji>
      <Text weight="semibold" color="white">
        {option.label}
      </Text>
    </OptionWithIconContainer>
  ) : (
    <OptionContainer onPress={handleSelect}>
      <Checkbox
        color={is_checked ? colors.primary[70] : colors.grey.dim}
        value={is_checked}
      />
      <Text weight="semibold" color="white">
        {option.label}
      </Text>
    </OptionContainer>
  );
};

const OptionContainer = styled.TouchableOpacity`
  flex-direction: row;
  gap: 10px;
  align-items: center;
`;

const OptionWithIconContainer = styled.TouchableOpacity<{ active?: boolean }>`
  gap: 10px;
  align-items: center;
  border-radius: 10px;
  padding: 5px;
  ${({ active }) => (active ? 'background-color: lightblue;' : '')}
`;

const Emoji = styled.Text`
  font-size: 50px;

  padding: 10px;
`;

const Container = styled(Shadow)`
  padding: 12px;
  margin-bottom: 12px;
  max-width: 80%;
  gap: 10px;
  background-color: ${color.secondary};
  margin-right: auto;
  border-radius: 10px;
  border-bottom-left-radius: 0;
`;

const ChatText = styled(Text)`
  font-size: 24px;
`;

export const styles = StyleSheet.create({
  option_row: {
    flexDirection: 'row',
    gap: 20,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
});

const Options = styled.View`
  gap: 10px;
`;

export default ChatTextItem;
